import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { buildGrantDiscoverSearchUrl, type GrantDiscoverFacets } from "@/hooks/grantDiscover/buildGrantDiscoverSearchUrl";
import { useGrantDiscoverAffinityProfile } from "@/hooks/grantDiscover/useGrantDiscoverAffinityProfile";
import { useGrantDiscoverRecommendations } from "@/hooks/grantDiscover/useGrantDiscoverRecommendations";
import { useGrantDiscoverSearch } from "@/hooks/grantDiscover/useGrantDiscoverSearch";
import { useGrantDiscoverTracking } from "@/hooks/grantDiscover/useGrantDiscoverTracking";

export { buildGrantDiscoverSearchUrl, type GrantDiscoverFacets };

type UseGrantDiscoverOptions = {
  municipalityId?: number;
  canUseGrantDiscover: boolean;
  canUseGrantPipeline: boolean;
  onSavedToWatchlist?: () => void;
  profileWizardOpen?: boolean;
  onProfileWizardOpenChange?: (open: boolean) => void;
};

export function useGrantDiscover({
  municipalityId,
  canUseGrantDiscover,
  canUseGrantPipeline,
  onSavedToWatchlist,
  profileWizardOpen: profileWizardOpenProp,
  onProfileWizardOpenChange,
}: UseGrantDiscoverOptions) {
  const queryClient = useQueryClient();
  const [internalProfileWizardOpen, setInternalProfileWizardOpen] = useState(false);
  const profileWizardOpen = profileWizardOpenProp ?? internalProfileWizardOpen;
  const setProfileWizardOpen = onProfileWizardOpenChange ?? setInternalProfileWizardOpen;
  const insightsSetterRef = useRef<(insights: string) => void>(() => {});
  const resetRecommendationPagingRef = useRef<() => void>(() => {});

  const profile = useGrantDiscoverAffinityProfile({ municipalityId, canUseGrantDiscover });

  const tracking = useGrantDiscoverTracking({
    municipalityId,
    canUseGrantPipeline,
    onSavedToWatchlist,
  });

  const search = useGrantDiscoverSearch({
    municipalityId,
    canUseGrantDiscover,
    profileComplete: profile.profileComplete,
    onInsights: (insights) => insightsSetterRef.current(insights),
    onRecommendationFiltersChange: () => resetRecommendationPagingRef.current(),
  });

  const recommendations = useGrantDiscoverRecommendations({
    municipalityId,
    canUseGrantDiscover,
    hasSearched: search.hasSearched,
    catalogScope: search.catalogScope,
    stateFilter: search.stateFilter,
    excludeFormula: search.excludeFormula,
    profileWizardOpen,
    setProfileWizardOpen,
    onFundingPrioritiesPrefill: search.setQuery,
    onDefaultSortFit: () => search.setSort("fit"),
    ...profile,
  });

  insightsSetterRef.current = recommendations.setRecommendedInsights;
  resetRecommendationPagingRef.current = recommendations.resetRecommendationPaging;

  const handleProfileSaved = () => {
    if (!municipalityId) return;
    recommendations.handleProfileSaved();
    search.setHasSearched(false);
    queryClient.invalidateQueries({ queryKey: ["grant_affinity_profile", municipalityId] });
  };

  return {
    ...profile,
    ...search,
    ...recommendations,
    ...tracking,
    profileWizardOpen,
    setProfileWizardOpen,
    handleProfileSaved,
  };
}
